import React from 'react';
import ReactDOM from 'react-dom';
import { BackgroundLayerSelection } from './BackgroundLayerSelection';
import { ThemeProvider } from 'oskari-ui/util';

/**
 * @class Oskari.mapframework.bundle.mapmodule.plugin.BackgroundLayerSelectionPlugin
 *
 * This is a plugin to bring more functionality for the mapmodules map
 * implementation. It provides a background maplayer selection "dropdown" on top of the map.
 *
 * See http://www.oskari.org/trac/wiki/DocumentationBundleMapModulePluginBackgroundLayerSelectionPlugin
 */
Oskari.clazz.define('Oskari.mapframework.bundle.mapmodule.plugin.BackgroundLayerSelectionPlugin',
/**
 * @static @method create called automatically on construction
 *
 *
 */
function () {
  var _this$_config;
  this._clazz = 'Oskari.mapframework.bundle.mapmodule.plugin.BackgroundLayerSelectionPlugin';
  this._defaultLocation = 'bottom center';
  this._index = 0;
  this._name = 'BackgroundLayerSelectionPlugin';
  // make sure baseLayers aren't numbers.
  this._baseLayerIds = (_this$_config = this._config) === null || _this$_config === void 0 || (_this$_config = _this$_config.baseLayers) === null || _this$_config === void 0 ? void 0 : _this$_config.map(function (id) {
    return typeof id === 'number' ? id.toString() : id;
  });
  this._baseLayerOptions = [];
  this._template = jQuery('<div class="backgroundLayerSelectionPlugin oskariui mapplugin"/>');
}, {
  /** @static @property __name module name */
  __name: 'BackgroundLayerSelectionPlugin',
  _initImpl: function _initImpl() {
    if (!this._getBaseLayerIds().length) {
      Oskari.log(this.getName()).error('No baseLayers defined in configuration');
    }
  },
  /**
   * @method _createEventHandlers
   * Create eventhandlers.
   *
   *
   * @return {Object.<string, Function>} EventHandlers
   */
  _createEventHandlers: function _createEventHandlers() {
    return {
      /**
       * @method AfterRearrangeSelectedMapLayerEvent
       * @param {Oskari.mapframework.event.common.AfterRearrangeSelectedMapLayerEvent} event
       *
       * Rearranges layers
       */
      AfterRearrangeSelectedMapLayerEvent: function AfterRearrangeSelectedMapLayerEvent() {
        // Update selection, bottom baselayer might've changed
        this.refresh();
      },
      /**
       * @method AfterMapLayerRemoveEvent
       * @param {Oskari.mapframework.event.common.AfterMapLayerRemoveEvent} event
       *
       * Removes the layer from selection
       */
      AfterMapLayerRemoveEvent: function AfterMapLayerRemoveEvent() {
        // Update selection, bottom baselayer might've changed
        this.refresh();
      },
      /**
       * @method AfterMapLayerAddEvent
       * @param {Oskari.mapframework.event.common.AfterMapLayerAddEvent} event
       *
       * Adds the layer to selection
       */
      AfterMapLayerAddEvent: function AfterMapLayerAddEvent() {
        // Update selection, bottom baselayer might've changed
        this.refresh();
      },
      /**
       * @method AfterMapMoveEvent
       * @param {Oskari.mapframework.event.common.AfterMapMoveEvent} event
       *
       * Adds the layer to selection
       */
      MapLayerEvent: function MapLayerEvent(event) {
        var _event$getLayerId;
        var layerId = (_event$getLayerId = event.getLayerId()) === null || _event$getLayerId === void 0 ? void 0 : _event$getLayerId.toString();
        if (layerId && !this._getBaseLayerIds().includes(layerId)) {
          // handle mass events and base layers only
          return;
        }
        if (event.getOperation() === 'update') {
          // clear to get updated options on refresh
          this._baseLayerOptions = [];
        }
        this.refresh();
      },
      MapSizeChangedEvent: function MapSizeChangedEvent() {
        this.refresh();
      }
    };
  },
  /**
   * @private @method _getBottomLayer
   * Returns the bottom-most selected layer if any
   *
   * @return {Oskari.mapframework.domain.WmsLayer[]/Oskari.mapframework.domain.WfsLayer[]/Oskari.mapframework.domain.VectorLayer[]/Mixed}
   */
  _getBottomLayerId: function _getBottomLayerId() {
    var _this$getSandbox$find;
    return (_this$getSandbox$find = this.getSandbox().findAllSelectedMapLayers()[0]) === null || _this$getSandbox$find === void 0 ? void 0 : _this$getSandbox$find.getId().toString();
  },
  _getSelectedId: function _getSelectedId() {
    var ids = this._getBaseLayerIds();
    // base layer is handled as base layer only when it's bottom layer
    var bottomId = this._getBottomLayerId();
    return ids.includes(bottomId) ? bottomId : undefined;
  },
  _getBaseLayerIds: function _getBaseLayerIds() {
    return this._baseLayerIds || [];
  },
  _getBaseLayerOptions: function _getBaseLayerOptions() {
    var _this = this;
    if (this._baseLayerOptions.length) {
      return this._baseLayerOptions;
    }
    var ids = this._getBaseLayerIds();
    var sb = this.getSandbox();
    var options = ids.map(function (id) {
      var _sb$findMapLayerFromA;
      return {
        id: id,
        title: (_sb$findMapLayerFromA = sb.findMapLayerFromAllAvailable(id)) === null || _sb$findMapLayerFromA === void 0 ? void 0 : _sb$findMapLayerFromA.getName(),
        action: function action() {
          return _this._onSelect(id);
        }
      };
    }).filter(function (opt) {
      return opt.title;
    });
    if (ids.length === options.length) {
      // store if we have full set
      this._baseLayerOptions = options;
    }
    return options;
  },
  /**
   * Does the actual layer selection update
   * @param  {String} newId Id of the new base layer
   * @private
   */
  _onSelect: function _onSelect(newId) {
    var selectedId = this._getSelectedId();
    if (newId === selectedId) {
      // user clicked already selected option, do nothing
      return;
    }
    var sb = this.getSandbox();
    // switch bg layer (no need to call update on ui, we should catch the event)
    // - check if current bottom layer exists & is in our list (if so, remove)
    if (selectedId) {
      sb.postRequestByName('RemoveMapLayerRequest', [selectedId]);
    }
    // - check if new selection is already selected, remove if so as rearrange doesn't seem to work
    if (sb.isLayerAlreadySelected(newId)) {
      sb.postRequestByName('RemoveMapLayerRequest', [newId]);
    }
    // - add to bottom
    sb.postRequestByName('AddMapLayerRequest', [newId, {
      toPosition: 0
    }]);
  },
  /**
   * @private @method  _createLayerSelectionElements
   * Creates LI elements for the bg layers.
   * These are used in both dropdown and list mode.
   *
   */
  _createLayerSelectionElements: function _createLayerSelectionElements() {
    var element = this.getElement();
    if (!element) {
      return;
    }
    ReactDOM.render(/*#__PURE__*/React.createElement(ThemeProvider, {
      value: this.getMapModule().getMapTheme()
    }, /*#__PURE__*/React.createElement(BackgroundLayerSelection, {
      isMobile: Oskari.util.isMobile(),
      baseLayers: this._getBaseLayerOptions(),
      selectedId: this._getSelectedId(),
      mapWidth: this.getSandbox().getMap().getWidth()
    })), element[0]);
  },
  _createControlElement: function _createControlElement() {
    return this._template.clone();
  },
  refresh: function refresh() {
    this._createLayerSelectionElements();
  }
}, {
  'extend': ['Oskari.mapping.mapmodule.plugin.BasicMapModulePlugin'],
  /**
   * @static @property {string[]} protocol array of superclasses
   */
  'protocol': ['Oskari.mapframework.module.Module', 'Oskari.mapframework.ui.module.common.mapmodule.Plugin']
});